import { useMemo, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import { useRootStore } from "store";
import config from "config/config";
import { Thread, InboxType } from "models/inbox/inbox";
import ThreadSidebarItem from "views/inbox/components/Inbox/sidebar/treadSidebarItem";
import { Button } from "components/button/Button";
import NoMessagesToShowInfoText from "../noMessagesToShowInfoText/noMessagesToShowInfoText";

type Props = {
  type: InboxType;
  threads: Thread[];
  shouldScroll: boolean;
  setShouldScroll: (value: boolean) => void;
};

export default function ThreadSidebarList({
  type,
  threads,
  shouldScroll,
  setShouldScroll
}: Props) {
  const workingCustomerId = useRootStore(
    (state) => state.global.workingCustomerId
  );
  const {
    sentMessagesSearchTerm,
    myTotalThreads,
    totalThreads,
    fetchMoreThreads
  } = useRootStore((state) => state.inbox);

  const userInfoId = useRootStore((state) => state.user.userInfo?.id ?? "");

  const threadsWithMeta = useMemo(() => {
    return threads.map((thread) => ({
      ...thread,
      subscriber: thread.subscribers.find((sub) => sub.id === userInfoId),
      threadLink: `/inbox/${type}/thread/${encodeURIComponent(thread.threadId)}`
    }));
  }, [threads, userInfoId, type]);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const activeThreadRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { threadId: currentThreadId } = useParams();
  // Scroll to the active thread when it changes
  useEffect(() => {
    if (currentThreadId && activeThreadRefs.current[currentThreadId]) {
      activeThreadRefs.current[currentThreadId]?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }, [currentThreadId, threadsWithMeta.length]);

  const threadsLeftToLoad =
    type === "my"
      ? myTotalThreads - threadsWithMeta.length
      : totalThreads - threadsWithMeta.length;

  const nextThreadsToLoadCount = Math.min(
    threadsLeftToLoad,
    config.defaults.pageSize
  );

  useEffect(() => {
    if (!shouldScroll || !bottomRef.current) {
      return;
    }

    bottomRef.current.scrollIntoView({ behavior: "smooth" });

    setShouldScroll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldScroll, threads.length]);

  const handleLoadMore = useCallback(() => {
    fetchMoreThreads(
      workingCustomerId,
      type !== "my",
      threads.length,
      sentMessagesSearchTerm
    );
    setShouldScroll(true);
  }, [
    type,
    threads.length,
    fetchMoreThreads,
    workingCustomerId,
    setShouldScroll,
    sentMessagesSearchTerm
  ]);

  if (!threads.length) {
    return <NoMessagesToShowInfoText />;
  }

  return (
    <>
      {threadsWithMeta.map((thread) => (
        <div
          key={thread.threadId}
          ref={(el) => {
            if (el) {
              activeThreadRefs.current[thread.threadId] = el;
            }
          }}
        >
          <ThreadSidebarItem
            thread={thread}
            isSubscribing={thread.subscriber !== undefined}
            threadLink={thread.threadLink}
          />
        </div>
      ))}

      {/*  Dummy element used for scrolling to more threads */}
      <div ref={bottomRef} style={{ height: 1 }} />

      {threadsLeftToLoad > 0 && (
        <div className="d-flex justify-content-center mt-3">
          <Button variant="outline" size="sm" onClick={handleLoadMore}>
            Visa nästa {nextThreadsToLoadCount} konversationer
          </Button>
        </div>
      )}
    </>
  );
}
